import { createTheme, ThemeProvider } from "@mui/material/styles";
import MUIDataTable from "mui-datatables";
import React, { useState, useEffect } from "react";
import {
    ResponsiveContainer,
    ComposedChart,
    XAxis,
    YAxis,
    Tooltip,
    Legend,
    Bar,
    Line,
    CartesianGrid,
} from "recharts";

import { earliestYearMonth, getMonthLabelsInRange } from "../../utils/monthRange";
import LoadingOverlay from "../LoadingOverlay";
import CustomLabel from "./CustomLabel";
import CustomTooltip from "./CustomTooltip";

const CumulativeCarrStackedChart = ({ props, onLoad, onDataStart, startMonth, endMonth }) => {
    const [carrData, setCarrData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Full history is fetched once; the selected month range is applied client-side
    const fetchMetrics = () => {
        setIsLoading(true);
        props
            .fetchData("getstandalone")
            .then((data) => {
                const metricsData = data || [];
                const cappedData = data.map((d) => ({
                    ...d,
                    netasset_yoy_capped: Math.max(0, Math.min(d.netasset_yoy, 100)),
                    netlease_yoy_capped: Math.max(0, Math.min(d.netlease_yoy, 100)),
                    total_yoy_capped: Math.max(0, Math.min(d.total_yoy, 100)),
                }));
                setCarrData(cappedData);
                if (onDataStart) onDataStart(earliestYearMonth(metricsData.map((d) => d.month)));
            })
            .catch((error) => console.error("Error fetching metrics:", error))
            .finally(() => {
                setIsLoading(false);
                if (onLoad) onLoad();
            });
    };

    useEffect(() => {
        fetchMetrics();
    }, []);

    const allowedMonths = getMonthLabelsInRange(startMonth, endMonth);
    const visibleData = carrData.filter((d) => allowedMonths.includes(d.month));

    return (
        <div className="mb-32">
            <h2>Standalone Business Unit</h2>
            <div className="pt-1">
                {isLoading ? (
                    onLoad ? null : (
                        <LoadingOverlay text="Loading metrics..." />
                    )
                ) : visibleData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={600}>
                        <ComposedChart data={visibleData}>
                            <CartesianGrid stroke="#f5f5f5" />
                            <XAxis dataKey="month" />
                            <YAxis
                                yAxisId="left"
                                domain={[0, 2.6]}
                                label={{
                                    value: "CARR (Millions)",
                                    angle: -90,
                                    position: "insideLeft",
                                    offset: 10,
                                    style: { textAnchor: "middle", fontSize: 13 },
                                }}
                            />
                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                domain={[0, 100]}
                                tickFormatter={(value) => (value === 100 ? "> 100%" : `${value}%`)}
                                label={{
                                    value: "YoY Growth (%)",
                                    angle: 90,
                                    position: "insideRight",
                                    offset: 10,
                                    style: { textAnchor: "middle", fontSize: 13 },
                                }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                            <Bar
                                yAxisId="left"
                                dataKey="netlease_carr"
                                name="NetLease Standalone"
                                barSize={20}
                                fill="#156082"
                                stackId="a"
                            />

                            <Bar
                                yAxisId="left"
                                dataKey="netasset_carr"
                                name="NetAsset Standalone"
                                barSize={20}
                                fill="#5b8fa9"
                                stackId="a"
                            />

                            <Line
                                yAxisId="right"
                                type="monotone"
                                name="NetLease Standalone YoY Growth (%)"
                                dataKey="netlease_yoy_capped"
                                stroke="#2f6f8f"
                                strokeWidth={3}
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                name="NetAsset YoY Growth (%)"
                                dataKey="netasset_yoy_capped"
                                stroke="#228B22"
                                strokeWidth={3}
                            />
                            <Line
                                yAxisId="right"
                                type="monotone"
                                name="TTL Standalone YoY Growth (%)"
                                dataKey="total_yoy_capped"
                                stroke="#66CDAA"
                                strokeWidth={3}
                            />

                            <Bar
                                yAxisId="left"
                                dataKey="total_carr"
                                name="Total CARR"
                                barSize={20}
                                fill="transparent"
                                stackId="b"
                                label={<CustomLabel data={visibleData} />}
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                ) : (
                    <h3 className="px-2 pt-8 text-center">No metrics available.</h3>
                )}
            </div>
        </div>
    );
};

export default CumulativeCarrStackedChart;
